import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import CreateProjectDialog from '../components/project/CreateProjectDialog';

function ProjectsPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Filter and sort state
  const [domain, setDomain] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadProjects();
  }, [domain, sortBy, sortOrder]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const params = {
        domain: domain || null,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      const data = await apiService.getProjects(params);
      // API now returns { projects: [...], total, skip, limit }
      setProjects(data.projects || data);
      setError(null);
    } catch (err) {
      setError('Failed to load projects');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setDomain('');
    setSortBy('created_at');
    setSortOrder('desc');
  };

  const hasActiveFilters = domain !== '' || sortBy !== 'created_at' || sortOrder !== 'desc';

  const handleDeleteProject = async (projectId, projectName) => {
    if (!window.confirm(`Are you sure you want to delete "${projectName}"? This will delete all associated files and transcriptions.`)) {
      return;
    }

    try {
      await apiService.deleteProject(projectId);
      setError(null);
      // Refresh projects list
      await loadProjects();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to delete project';
      setError(`Failed to delete project: ${errorMessage}`);
      console.error('Delete project error:', err);
    }
  };

  const handleProjectCreated = (newProject) => {
    // Refresh projects list
    loadProjects();
    // Optionally navigate to the new project
    // navigate(`/projects/${newProject.id}`);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Projects
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Toggle filters">
            <IconButton
              onClick={() => setShowFilters(!showFilters)}
              color={showFilters || hasActiveFilters ? 'primary' : 'default'}
            >
              <FilterIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            New Project
          </Button>
        </Box>
      </Box>

      {/* Filters Panel */}
      {showFilters && (
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FilterIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Filters & Sorting</Typography>
            {hasActiveFilters && (
              <Tooltip title="Clear all filters">
                <IconButton
                  size="small"
                  onClick={handleClearFilters}
                  sx={{ ml: 'auto' }}
                >
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <Grid container spacing={2}>
            {/* Domain Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Domain</InputLabel>
                <Select
                  value={domain}
                  label="Domain"
                  onChange={(e) => setDomain(e.target.value)}
                >
                  <MenuItem value="">All Domains</MenuItem>
                  <MenuItem value="general">General</MenuItem>
                  <MenuItem value="medical">Medical</MenuItem>
                  <MenuItem value="legal">Legal</MenuItem>
                  <MenuItem value="technical">Technical</MenuItem>
                  <MenuItem value="history">History</MenuItem>
                  <MenuItem value="business">Business</MenuItem>
                  <MenuItem value="academic">Academic</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Sort By */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="created_at">Date Created</MenuItem>
                  <MenuItem value="name">Name</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Sort Order */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Order</InputLabel>
                <Select
                  value={sortOrder}
                  label="Order"
                  onChange={(e) => setSortOrder(e.target.value)}
                >
                  <MenuItem value="asc">Ascending</MenuItem>
                  <MenuItem value="desc">Descending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {hasActiveFilters && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Active filters: {domain && `Domain: ${domain}`}{' '}
                {sortBy !== 'created_at' && `Sort: ${sortBy}`}{' '}
                {sortOrder !== 'desc' && `Order: ${sortOrder}`}
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : projects.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary" align="center">
              No projects yet. Create your first project to get started!
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {projects.map((project) => (
            <Grid item xs={12} md={6} lg={4} key={project.id}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {project.name}
                  </Typography>
                  {project.domain && (
                    <Chip label={project.domain} size="small" sx={{ mb: 1 }} />
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {project.description || 'No description'}
                  </Typography>
                  {project.interviewer_name && (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Interviewer: {project.interviewer_name}
                    </Typography>
                  )}
                </CardContent>
                <CardActions>
                  <Button size="small" onClick={() => navigate(`/projects/${project.id}`)}>
                    Open
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteProject(project.id, project.name);
                    }}
                  >
                    Delete
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <CreateProjectDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onProjectCreated={handleProjectCreated}
      />
    </Box>
  );
}

export default ProjectsPage;
